/**
 * Sdílený slovník a formátování sekce IoT (spec UX IoT 2026-10-06, oddíl 1).
 * Všechny záložky a stránky IoT berou texty stavů odsud, aby se stejný stav jmenoval všude stejně.
 */
import { SCHVALENI, VYSVETLENI_SCHVALENI, type AkcePravidla, type IotPravidlo, type IotZarizeni, type StavKanalu, type StavZarizeni, type TypKanalu } from '../../api/JsdhNotifikaceService/service/IotZarizeniService';

// ---------------------------------------------------------------------------
// Čas „naposledy“ (oddíl 1.1)
// ---------------------------------------------------------------------------

const MINUTA = 60 * 1000;

const hodinyMinuty = (d: Date): string => `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
const stejnyDen = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Plné datum a čas pro tooltip: „6. 10. 2026 23:51“. */
export const formatPlnyCas = (ms: number): string => {
    const d = new Date(ms);
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${hodinyMinuty(d)}`;
};

/**
 * Relativní čas: „právě teď“, „před 12 min“, „dnes 23:51“, „včera 23:51“, „5. 10. 23:51“,
 * v jiném roce „5. 10. 2025 23:51“.
 */
export const formatNaposledy = (ms: number, now: number = Date.now()): string => {
    const rozdil = now - ms;
    if (rozdil < MINUTA) return 'právě teď';
    if (rozdil < 60 * MINUTA) return `před ${Math.floor(rozdil / MINUTA)} min`;
    const d = new Date(ms);
    const ted = new Date(now);
    if (stejnyDen(d, ted)) return `dnes ${hodinyMinuty(d)}`;
    const vcera = new Date(ted.getFullYear(), ted.getMonth(), ted.getDate() - 1);
    if (stejnyDen(d, vcera)) return `včera ${hodinyMinuty(d)}`;
    if (d.getFullYear() === ted.getFullYear()) return `${d.getDate()}. ${d.getMonth() + 1}. ${hodinyMinuty(d)}`;
    return formatPlnyCas(ms);
};

// ---------------------------------------------------------------------------
// Stav spojení (oddíl 1.1)
// ---------------------------------------------------------------------------

export type DruhSpojeni = 'online' | 'nedostupne' | 'neozvalo' | 'neznamy' | 'bez-stavu';

export interface StavSpojeni {
    druh: DruhSpojeni;
    /** Text štítku nebo řádku; prázdný u `bez-stavu` (nic nezobrazovat). */
    text: string;
    barva: 'default' | 'warning';
    tooltip?: string;
}

export interface VstupSpojeni {
    /** true = online, false = nedostupné, null/undefined = nevíme. */
    online?: boolean | null;
    posledniKontaktMs?: number | null;
    /** Dotaz na stav skončil chybou (např. 502 z /zarizeni/{id}/stav). */
    chyba?: boolean;
}

export const TOOLTIP_STAV_NEZNAMY = 'Server pro zařízení teď neodpovídá. Stav se načte, až bude dostupný.';

/** Práh online u čidel: kontakt za posledních 30 min (pravidlo isOnline v IoTObjektDetailPage, neměnit). */
export const PRAH_ONLINE_CIDLA_MS = 30 * MINUTA;

/**
 * Jedna funkce pro stav spojení zařízení Shelly, čidel, ústředen i RPi.
 * `null`/`undefined` vstup = protokol bez živého stavu (nic se nezobrazí).
 */
export const stavSpojeni = (v: VstupSpojeni | null | undefined, now: number = Date.now()): StavSpojeni => {
    if (!v) return { druh: 'bez-stavu', text: '', barva: 'default' };
    if (v.chyba) return { druh: 'neznamy', text: 'Stav nelze zjistit', barva: 'default', tooltip: TOOLTIP_STAV_NEZNAMY };
    const kontakt = v.posledniKontaktMs ?? null;
    if (v.online === true) {
        return { druh: 'online', text: 'Online', barva: 'default', tooltip: kontakt != null ? `Naposledy se ozvalo ${formatPlnyCas(kontakt)}` : undefined };
    }
    if (v.online === false) {
        return {
            druh: 'nedostupne', barva: 'warning',
            text: kontakt != null ? `Nedostupné · naposledy ${formatNaposledy(kontakt, now)}` : 'Nedostupné',
            tooltip: kontakt != null ? `Naposledy se ozvalo ${formatPlnyCas(kontakt)}` : undefined,
        };
    }
    if (kontakt == null) return { druh: 'neozvalo', text: 'Zatím se neozvalo', barva: 'default' };
    // Server zařízení zná, ale o spojení nic neví: neříkáme online ani nedostupné.
    return { druh: 'neznamy', text: 'Stav nelze zjistit', barva: 'default', tooltip: `Naposledy se ozvalo ${formatPlnyCas(kontakt)}` };
};

/** Zařízení Shelly: stav z /zarizeni/{id}/stav. Bez dat a bez chyby (načítá se) = nic nezobrazovat. */
export const stavSpojeniShelly = (stav: StavZarizeni | undefined, chyba: boolean, now: number = Date.now()): StavSpojeni =>
    stavSpojeni(chyba ? { chyba: true } : stav ? { online: stav.online, posledniKontaktMs: stav.posledniKontaktEpochMs } : null, now);

/**
 * Stav spojení v tabulce evidence (K11a): `online` a `posledniKontaktEpochMs` rozhodl BE. Stejné znění jako
 * v detailu objektu (bug-inspector P1): obě null = „Zatím se neozvalo“ (např. ústředna SIA ve stavu UNKNOWN),
 * null s kontaktem = „Stav nelze zjistit“. Do „Nedostupných“ patří jen `online === false`.
 */
export const stavSpojeniEvidence = (
    z: { online?: boolean | null; posledniKontaktEpochMs?: number | null }, now: number = Date.now(),
): StavSpojeni => stavSpojeni({ online: z.online, posledniKontaktMs: z.posledniKontaktEpochMs }, now);

/**
 * Nedostupné záznamy evidence (`online === false` podle BE). Jediný výpočet pro hlavičku objektu i čip
 * v záložce Zařízení, aby se obě čísla nikdy nerozešla (E1).
 */
export const jeNedostupneEvidence = (z: { online?: boolean | null; posledniKontaktEpochMs?: number | null }): boolean =>
    stavSpojeniEvidence(z).druh === 'nedostupne';

export const pocetNedostupnychEvidence = (zarizeni: { online?: boolean | null; posledniKontaktEpochMs?: number | null }[]): number =>
    zarizeni.filter(jeNedostupneEvidence).length;

/** Čidlo (Zigbee/Shelly): online = kontakt za posledních 30 min. */
export const stavSpojeniCidla = (posledniKontakt: string | number | null | undefined, now: number = Date.now()): StavSpojeni => {
    if (posledniKontakt == null || posledniKontakt === '') return stavSpojeni({ online: null, posledniKontaktMs: null }, now);
    const ms = typeof posledniKontakt === 'number' ? posledniKontakt : new Date(posledniKontakt).getTime();
    if (Number.isNaN(ms)) return stavSpojeni({ online: null, posledniKontaktMs: null }, now);
    return stavSpojeni({ online: ms > now - PRAH_ONLINE_CIDLA_MS, posledniKontaktMs: ms }, now);
};

// ---------------------------------------------------------------------------
// Schválení správcem systému a štítky (oddíl 1.2)
// ---------------------------------------------------------------------------

export interface Stitek {
    klic: 'NOVE' | 'ZAMITNUTO' | 'IDENTITA' | 'NEOVERENO' | 'NEAKTIVNI';
    text: string;
    barva: 'default' | 'warning' | 'error';
    variant: 'filled' | 'outlined';
    tooltip?: string;
}

export const TOOLTIP_NEOVERENY_MODEL = 'Tento model jsme zatím neotestovali. Zařízení funguje, jen jeho chování nemáme ověřené.';
export const TOOLTIP_NEDORESENA_IDENTITA = 'Server pro zařízení zatím nepotvrdil, pod jakým ID čidlo zná. Záznam drží původní ID '
    + 'a opraví se sám při dalším dorovnání se serverem.';

type ZarizeniProStitky = Pick<IotZarizeni, 'stavSchvaleni' | 'identitaNedoresena' | 'overeno' | 'aktivni'>;

/**
 * Upozornění na schválení správcem systému (Čeká na schválení / Zamítnuto). Jen informuje, nic neblokuje;
 * SCHVALENO a neznámý stav (null) nevrací nic.
 */
export const stitekSchvaleniSpravcem = (stavSchvaleni: string | null | undefined): Stitek | null => {
    if (stavSchvaleni !== 'NOVE' && stavSchvaleni !== 'ZAMITNUTO') return null;
    const sch = SCHVALENI[stavSchvaleni];
    return { klic: stavSchvaleni, text: sch.labelKarta, barva: sch.barvaKarta, variant: 'filled', tooltip: VYSVETLENI_SCHVALENI };
};

/** Štítky karty zařízení podle 1.2. Normální stav (schváleno) nevrací nic. */
export const stitekSchvaleni = (z: ZarizeniProStitky): Stitek[] => {
    const vysledek: Stitek[] = [];
    const sch = stitekSchvaleniSpravcem(z.stavSchvaleni);
    if (sch) vysledek.push(sch);
    if (z.identitaNedoresena) {
        vysledek.push({ klic: 'IDENTITA', text: 'Nedořešená identita', barva: 'warning', variant: 'filled', tooltip: TOOLTIP_NEDORESENA_IDENTITA });
    }
    if (z.overeno !== true) vysledek.push({ klic: 'NEOVERENO', text: 'Neověřený model', barva: 'default', variant: 'outlined', tooltip: TOOLTIP_NEOVERENY_MODEL });
    if (!z.aktivni) vysledek.push({ klic: 'NEAKTIVNI', text: 'Neaktivní', barva: 'default', variant: 'outlined' });
    return vysledek;
};

// ---------------------------------------------------------------------------
// Názvy pojmů (oddíl 1.4)
// ---------------------------------------------------------------------------

const NAZEV_TYPU_SENZORU: Record<string, string> = {
    SMOKE: 'Detektor kouře', TEMPERATURE: 'Teploměr', CO: 'Detektor CO', WATER_LEAK: 'Detektor zaplavení',
};
/** Typ čidla česky; neznámý typ „Neznámý typ (WEIRD)“, prázdný „Neznámý typ“ (1.4). */
export const nazevTypuSenzoru = (typ: string | null | undefined): string => {
    if (!typ) return 'Neznámý typ';
    return NAZEV_TYPU_SENZORU[typ] ?? `Neznámý typ (${typ})`;
};

const NAZEV_TYPU_VYSTUPU: Record<string, string> = { switch: 'Výstup', light: 'Světelný výstup', cover: 'Roleta' };

/** Technický kód komponenty na srozumitelný název: switch:0 → Výstup 1, light:0 → Světelný výstup 1, cover:0 → Roleta 1. */
export const nazevVystupu = (komponenta: string): string => {
    const [typ, cislo] = komponenta.split(':');
    const n = Number(cislo);
    const nazev = NAZEV_TYPU_VYSTUPU[typ];
    if (!nazev || cislo === undefined || cislo === '' || !Number.isInteger(n) || n < 0) return komponenta;
    return `${nazev} ${n + 1}`;
};

/** Název s technickým kódem v závorce (jen dialog přidání výstupu a Technické údaje): „Výstup 1 (switch:0)“. */
export const nazevVystupuSKodem = (komponenta: string): string => {
    const nazev = nazevVystupu(komponenta);
    return nazev === komponenta ? komponenta : `${nazev} (${komponenta})`;
};

const STAV_TEXT: Record<string, string> = { open: 'Otevřeno', closed: 'Zavřeno', stopped: 'Zastaveno' };

/** Stav rolety z gateway česky; neznámou hodnotu vrátí beze změny. */
export const stavTextCesky = (stavText: string): string => STAV_TEXT[stavText] ?? stavText;

const TYPY_TEPLOTY = new Set(['HIGH_TEMP', 'TEMPERATURE']);
const TYPY_BEZ_HODNOTY = new Set(['SMOKE', 'CO']);
const KLICE_TEPLOTY = ['temperature', 'teplota', 'tC', 'value'];

const cislo = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v.replace(',', '.')))) return Number(v.replace(',', '.'));
    return null;
};

/**
 * Hodnota události čidla pro zobrazení: teplota „21,5 °C“, kouř a CO bez hodnoty, neznámý JSON se
 * nezobrazí (null). Prostý text, který není JSON, se vrátí beze změny.
 */
export const popisHodnoty = (typ: string, hodnota: string | null | undefined): string | null => {
    if (hodnota == null || hodnota.trim() === '' || TYPY_BEZ_HODNOTY.has(typ)) return null;
    const text = hodnota.trim();
    let json: unknown;
    let jeJson = false;
    if (text.startsWith('{') || text.startsWith('[')) {
        try { json = JSON.parse(text); jeJson = true; } catch { jeJson = false; }
    }
    if (TYPY_TEPLOTY.has(typ)) {
        let t: number | null = null;
        if (jeJson && json && typeof json === 'object' && !Array.isArray(json)) {
            const o = json as Record<string, unknown>;
            t = KLICE_TEPLOTY.map((k) => cislo(o[k])).find((x) => x != null) ?? null;
        } else if (!jeJson) t = cislo(text);
        return t == null ? null : `${t.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} °C`;
    }
    if (jeJson || text.startsWith('{') || text.startsWith('[')) return null;
    return text;
};

// ---------------------------------------------------------------------------
// Pravidlo při poplachu (oddíl 1.3)
// ---------------------------------------------------------------------------

/** Popisky akcí pravidla; u světla „Rozsvítit“ místo „Zapnout“. */
export const labelAkce = (a: AkcePravidla, typ: TypKanalu): string => ({
    ZAPNOUT_NA_MINUT: typ === 'light' ? 'Rozsvítit na N minut' : 'Zapnout na N minut',
    PULS: 'Puls N sekund',
    ZAPNOUT_DO_KONCE: typ === 'light' ? 'Rozsvítit do konce poplachu' : 'Zapnout do konce poplachu',
    VYPNOUT: typ === 'light' ? 'Zhasnout' : 'Vypnout',
    COVER_OTEVRIT: 'Otevřít', COVER_ZAVRIT: 'Zavřít', COVER_STOP: 'Zastavit', COVER_POZICE: 'Nastavit polohu',
}[a]);

export interface CastiPravidla {
    /** Akce: „rozsvítit na 80 %“, „zapnout na 30 min“, „otevřít“. */
    akce: string;
    /** Co po skončení: „po skončení vrátit původní stav“ / „po skončení nechat, jak je“; prázdné, když se neřeší. */
    konec: string;
    /** „· i při testovacím poplachu“ nebo prázdné. */
    test: string;
    /** Pravidlo je vypnuté (zobrazit šedě s „· vypnuto“). */
    vypnuto: boolean;
}

/** Části věty pravidla pro řádek „Při poplachu: …“ (1.3); null = pravidlo není nastavené. */
export const castiPravidla = (typ: TypKanalu, p: IotPravidlo | null | undefined): CastiPravidla | null => {
    if (!p) return null;
    const n = p.parametr;
    const zapnout = typ === 'light' ? 'rozsvítit' : 'zapnout';
    const akce = p.akce === 'ZAPNOUT_NA_MINUT' && n != null ? `${zapnout} na ${n} min`
        : p.akce === 'PULS' && n != null ? `puls ${n} s`
            : p.akce === 'COVER_POZICE' && n != null ? `nastavit polohu na ${n} %`
                : p.akce === 'ZAPNOUT_DO_KONCE' && typ === 'light' && n != null ? `rozsvítit na ${n} %`
                    : p.akce === 'ZAPNOUT_DO_KONCE' ? zapnout
                        : labelAkce(p.akce, typ).toLowerCase();
    const konec = typ === 'cover' || p.akce === 'ZAPNOUT_NA_MINUT' || p.akce === 'PULS' ? ''
        : p.poKonci === 'VRATIT' ? 'po skončení vrátit původní stav' : 'po skončení nechat, jak je';
    return { akce, konec, test: p.vcetneTestu ? '· i při testovacím poplachu' : '', vypnuto: !p.aktivni };
};

/** Věta pravidla bez předpony, jak se sází do řádku: „rozsvítit na 80 %, po skončení vrátit původní stav“. */
export const vetaPravidla = (c: CastiPravidla): string => (c.konec ? `${c.akce}, ${c.konec}` : c.akce);

/** Celý text řádku podle 1.3, např. „Při poplachu: rozsvítit na 80 % · vypnuto“. */
export const textRadkuPravidla = (typ: TypKanalu, p: IotPravidlo | null | undefined): string => {
    const c = castiPravidla(typ, p);
    if (!c) return 'Při poplachu: nenastaveno';
    return `Při poplachu: ${vetaPravidla(c)}${c.test ? ` ${c.test}` : ''}${c.vypnuto ? ' · vypnuto' : ''}`;
};

// ---------------------------------------------------------------------------
// Stav výstupu (oddíl 2)
// ---------------------------------------------------------------------------

/** Okamžitý stav výstupu slovy: „zapnuto“, „vypnuto“, „rozsvíceno 60 %“, „otevřeno“; null = stav neznámý. */
export const popisStavuVystupu = (typ: TypKanalu, s: StavKanalu | undefined): string | null => {
    if (!s) return null;
    if (typ === 'cover') {
        const casti = [s.stavText ? (STAV_TEXT[s.stavText] ? STAV_TEXT[s.stavText].toLowerCase() : s.stavText) : null,
            s.hodnota != null ? `poloha ${s.hodnota} %` : null].filter(Boolean);
        if (casti.length) return casti.join(', ');
        return s.vystup == null ? null : s.vystup ? 'zapnuto' : 'vypnuto';
    }
    if (typ === 'light') {
        if (s.vystup === false) return 'zhasnuto';
        if (s.vystup === true) return s.hodnota != null ? `rozsvíceno ${s.hodnota} %` : 'rozsvíceno';
        if (s.stavText) return s.stavText;
        return s.hodnota != null ? `jas ${s.hodnota} %` : null;
    }
    if (s.vystup != null) return s.vystup ? 'zapnuto' : 'vypnuto';
    return s.stavText || null;
};

/** Příkon a energie menším písmem za stavem: „12 W · 1,23 kWh“; null = nic neměří. */
export const popisMereni = (s: StavKanalu | undefined): string | null => {
    if (!s) return null;
    const casti = [
        s.apowerW != null ? `${s.apowerW.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} W` : null,
        s.energieWh != null ? `${(s.energieWh / 1000).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh` : null,
    ].filter(Boolean);
    return casti.length ? casti.join(' · ') : null;
};

export interface ZobrazeniStavuVystupu {
    /** Hlavní text stavu. */
    text: string;
    /** Šedě (poslední známý nebo neznámý stav). */
    sede: boolean;
    /** Příkon a energie; jen u dostupného zařízení. */
    mereni: string | null;
    /** Chyba výstupu ze zařízení: jediný červený štítek. */
    chyba: string | null;
}

/**
 * Jak ukázat stav výstupu podle spojení (oddíl 2): online normálně s měřením, nedostupné šedě
 * „poslední známý stav: …“ bez měření, jinak „stav neznámý“.
 */
export const zobrazeniStavuVystupu = (typ: TypKanalu, s: StavKanalu | undefined, spojeni: DruhSpojeni): ZobrazeniStavuVystupu => {
    const chyba = s?.chyby ? `Chyba: ${s.chyby}` : null;
    const popis = popisStavuVystupu(typ, s);
    if (spojeni === 'nedostupne') {
        return { text: popis ? `poslední známý stav: ${popis}` : 'stav neznámý', sede: true, mereni: null, chyba };
    }
    if (spojeni === 'online' && popis) return { text: popis, sede: false, mereni: popisMereni(s), chyba };
    return { text: 'stav neznámý', sede: true, mereni: null, chyba };
};

// ---------------------------------------------------------------------------
// Rozpoznání (oddíl 2, bod 5) - jen v Technických údajích
// ---------------------------------------------------------------------------

/** Absolutní datum a čas bez roku v aktuálním roce: „5. 10. 23:40“, jinak „5. 10. 2025 23:40“. */
export const formatDatumCas = (ms: number, now: number = Date.now()): string => {
    const d = new Date(ms);
    return d.getFullYear() === new Date(now).getFullYear() ? `${d.getDate()}. ${d.getMonth() + 1}. ${hodinyMinuty(d)}` : formatPlnyCas(ms);
};

const PROFIL: Record<string, string> = { switch: 'profil spínače', cover: 'profil rolety', light: 'profil světla' };
const SKUPINA_KOMPONENT: Record<string, [string, string]> = {
    switch: ['výstup', 'výstupy'], light: ['světelný výstup', 'světelné výstupy'], cover: ['roleta', 'rolety'], input: ['vstup', 'vstupy'],
};

const vyjmenuj = (cisla: number[]): string =>
    cisla.length <= 1 ? cisla.join('') : `${cisla.slice(0, -1).join(', ')} a ${cisla[cisla.length - 1]}`;

/** Rozpoznané komponenty slovy: „výstupy 1 a 2, vstupy 1 a 2“; neznámé typy technickým kódem. */
export const popisKomponent = (komponenty: string[]): string => {
    const skupiny = new Map<string, number[]>();
    const ostatni: string[] = [];
    komponenty.forEach((k) => {
        const [typ, c] = k.split(':');
        const n = Number(c);
        if (SKUPINA_KOMPONENT[typ] && c !== undefined && c !== '' && Number.isInteger(n) && n >= 0) skupiny.set(typ, [...(skupiny.get(typ) ?? []), n + 1]);
        else ostatni.push(k);
    });
    const casti = Array.from(skupiny.entries()).map(([typ, cisla]) => `${SKUPINA_KOMPONENT[typ][cisla.length > 1 ? 1 : 0]} ${vyjmenuj(cisla)}`);
    return casti.concat(ostatni).join(', ');
};

/**
 * Řádky rozpoznání pro Technické údaje: poslední výsledek s časem. Gateway čas neúspěchu neposílá,
 * proto se neúspěch ukazuje bez času; když je vedle neúspěchu i úspěšné rozpoznání, bereme neúspěch
 * jako novější a úspěch označíme „(starší)“.
 */
export const popisRozpoznani = (stav: StavZarizeni | undefined, now: number = Date.now()): string[] => {
    if (!stav) return [];
    const radky: string[] = [];
    if (stav.rozpoznaniChyba) radky.push(`Poslední rozpoznání se nepovedlo: ${stav.rozpoznaniChyba}`);
    if (stav.modelKod) {
        const kdy = stav.rozpoznanoEpochMs != null ? ` ${formatDatumCas(stav.rozpoznanoEpochMs, now)}` : '';
        const casti = [stav.modelKod, stav.profil ? PROFIL[stav.profil] ?? `profil ${stav.profil}` : null,
            (stav.komponenty ?? []).length ? popisKomponent(stav.komponenty) : null].filter(Boolean);
        radky.push(`Rozpoznáno${kdy}: ${casti.join(', ')}${stav.rozpoznaniChyba ? ' (starší)' : ''}`);
    }
    return radky;
};

/** Celá věta shrnutí v dialogu pravidla: „Při poplachu se Světlo A rozsvítí na 100 % a po skončení poplachu se vrátí do původního stavu.“ */
export const shrnutiPravidla = (nazev: string, typ: TypKanalu, p: IotPravidlo): string => {
    const n = p.parametr == null ? '…' : String(p.parametr);
    const svetlo = typ === 'light';
    const sloveso = {
        ZAPNOUT_DO_KONCE: svetlo ? (p.parametr != null ? `rozsvítí na ${n} %` : 'rozsvítí') : 'zapne',
        ZAPNOUT_NA_MINUT: `${svetlo ? 'rozsvítí' : 'zapne'} na ${n} min`,
        PULS: `sepne pulsem na ${n} s`,
        VYPNOUT: svetlo ? 'zhasne' : 'vypne',
        COVER_OTEVRIT: 'otevře', COVER_ZAVRIT: 'zavře', COVER_STOP: 'zastaví', COVER_POZICE: `nastaví na polohu ${n} %`,
    }[p.akce];
    const c = castiPravidla(typ, p);
    const konec = !c?.konec ? '' : p.poKonci === 'VRATIT'
        ? ' a po skončení poplachu se vrátí do původního stavu' : ' a po skončení poplachu zůstane, jak je';
    return `Při poplachu se ${nazev} ${sloveso}${konec}.`
        + (p.vcetneTestu ? ' Provede se i při testovacím poplachu.' : '')
        + (p.aktivni ? '' : ' Pravidlo je vypnuté, při poplachu se teď neprovede.');
};

// ---------------------------------------------------------------------------
// Detail objektu: řádek stavu a skupiny zařízení (oddíly 4.1 a 4.2)
// ---------------------------------------------------------------------------

/** Kategorie, které se při poplachu ovládají, i když zatím nemají výstup (skupina A v záložce Zařízení). */
export const KATEGORIE_OVLADANE = ['ZASUVKA', 'MODUL_1K', 'MODUL_2K', 'STMIVAC'];

/** Skupina A „Ovládané při poplachu“: má výstupy nebo je v ovládané kategorii. */
export const jeOvladaneZarizeni = (z: Pick<IotZarizeni, 'kanaly' | 'kategorie'>): boolean =>
    z.kanaly.length > 0 || KATEGORIE_OVLADANE.includes(z.kategorie);

/** Česká tvar podle počtu: 1 / 2–4 / 0 a 5+. */
export const tvarPoctu = (n: number, jeden: string, dva: string, pet: string): string =>
    n === 1 ? jeden : n >= 2 && n <= 4 ? dva : pet;

/** „1 zařízení nedostupné“, „3 zařízení nedostupná“, „5 zařízení nedostupných“. */
export const textNedostupnaZarizeni = (n: number): string =>
    `${n} zařízení ${tvarPoctu(n, 'nedostupné', 'nedostupná', 'nedostupných')}`;

/** „1 čidlo nedostupné“, „2 čidla nedostupná“, „5 čidel nedostupných“. */
export const textNedostupnaCidla = (n: number): string =>
    `${n} ${tvarPoctu(n, 'čidlo nedostupné', 'čidla nedostupná', 'čidel nedostupných')}`;

/** Údaje o RPi objektu z BE. `rpiStatus` (stáří kontaktu) má přednost před surovým `rpiOnline`. */
export interface RpiObjektu { rpiOnline?: boolean | null; rpiStatus?: string | null; rpiLastOnline?: string | null }

const rpiHlasiOnline = (o: RpiObjektu): boolean => (o.rpiStatus != null ? o.rpiStatus === 'online' : !!o.rpiOnline);

/**
 * RPi se zatím neozvalo: bez posledního kontaktu. BE pak posílá `offline_long`, ale „nedostupné“ to není,
 * nikdy nebylo dostupné (stejně jako čidlo bez kontaktu, 1.1).
 */
export const rpiNeozvalo = (o: RpiObjektu): boolean => !o.rpiLastOnline && !rpiHlasiOnline(o);

/** RPi je nedostupné jen se známým posledním kontaktem a stavem jiným než online. */
export const rpiNedostupne = (o: RpiObjektu): boolean => !!o.rpiLastOnline && !rpiHlasiOnline(o);

/** RPi v hlavičce objektu (1.1): „Raspberry Pi: online“ / „Raspberry Pi: nedostupné · naposledy …“. */
export const stavSpojeniRpi = (rpiOnline: boolean, rpiLastOnline: string | null | undefined, now: number = Date.now()): StavSpojeni => {
    const ms = rpiLastOnline ? new Date(rpiLastOnline).getTime() : null;
    const s = stavSpojeni({ online: rpiOnline, posledniKontaktMs: ms != null && !Number.isNaN(ms) ? ms : null }, now);
    return { ...s, text: `Raspberry Pi: ${s.text.charAt(0).toLowerCase()}${s.text.slice(1)}` };
};

/** Ústředna EPS/EZS (SIA): ONLINE / OFFLINE z watchdogu, jinak nevíme (1.1). */
export const stavSpojeniSia = (
    a: { onlineStatus?: string | null; posledniKontakt?: string | null },
    now: number = Date.now(),
): StavSpojeni => {
    const ms = a.posledniKontakt ? new Date(a.posledniKontakt).getTime() : null;
    const online = a.onlineStatus === 'ONLINE' ? true : a.onlineStatus === 'OFFLINE' ? false : null;
    return stavSpojeni({ online, posledniKontaktMs: ms != null && !Number.isNaN(ms) ? ms : null }, now);
};

/** Čip „čeká na schválení“ v souhrnu záložky Zařízení (4.2): zařízení čeká na schválení správcem systému. */
export const cekaNaSchvaleni = (z: Pick<IotZarizeni, 'stavSchvaleni'>): boolean => z.stavSchvaleni === 'NOVE';
